import { Autocomplete, Skeleton, TextField } from '@mui/material';
import { Schema as AccountSchema } from 'further-types/lp/account';
import { AccountSectionLabels } from 'further-ui/labels';
import api from 'lib/trpcClient';

type Props = {
  vehicleId: string;
  disabled?: boolean;
  name?: string;
  error?: {
    message?: string;
  };
  value?: string;
  onChange: (
    value: string,
    accountDetails: {
      section: string;
      sectionLabel: string;
      label: string;
      value: string;
      payable?: boolean;
      receivable?: boolean;
    },
  ) => void;
  withoutPayablesAndReceivables?: boolean;
};

const TransactionTypeSelector: React.FC<Props> = ({
  vehicleId,
  disabled,
  name,
  error,
  value,
  onChange,
  withoutPayablesAndReceivables,
}) => {
  const { data: accounts, isLoading } = api.account.listAllAccounts.useQuery({
    vehicleId,
  });

  const accountsSections = Object.keys(accounts ?? {});

  const options = accountsSections.flatMap((section) => {
    return (accounts?.[section] ?? []).flatMap((account) => {
      const isPayable = AccountSchema.isPayableCategory(
        account.payableReceivableCategory,
      );
      const isReceivable = AccountSchema.isReceivableCategory(
        account.payableReceivableCategory,
      );

      return account.transactionTypes
        .map((transactionType) => {
          return {
            section,
            sectionLabel:
              AccountSectionLabels[section as AccountSchema.AccountSection],
            label: `${account.accountCode} ${account.accountName} - ${transactionType.name}`,
            value: transactionType.id,
            payable: isPayable,
            receivable: isReceivable,
          };
        })
        .filter(
          (option) =>
            !withoutPayablesAndReceivables ||
            (!option.payable && !option.receivable),
        );
    });
  });

  if (isLoading) {
    return <Skeleton variant="rounded" height={41} animation="wave" />;
  }

  return (
    <Autocomplete
      fullWidth
      disableClearable
      disabled={!!disabled || isLoading}
      options={options}
      placeholder="Select transaction type"
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size="small"
          name={name}
          error={!!error}
          helperText={error?.message}
          type="text"
          placeholder="Select transaction type"
        />
      )}
      groupBy={(option) => option.sectionLabel}
      value={options.find((option) => option.value === value)}
      onChange={(_, newValue) => {
        onChange(newValue.value, {
          section: newValue.section,
          sectionLabel: newValue.sectionLabel,
          label: newValue.label,
          value: newValue.value,
          payable: newValue.payable,
          receivable: newValue.receivable,
        });
      }}
    />
  );
};

export default TransactionTypeSelector;
